import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Stage, Layer, Image as KonvaImage, Circle, Text } from 'react-konva';
import useImage from 'use-image';
import { v4 as uuidv4 } from 'uuid';

import { ArrowUturnLeftIcon, XMarkIcon } from '@heroicons/react/24/solid';

import ConfirmationModal from 'components/ConfirmationModal';

// Cap the canvas height so very tall plans don't take over the page; width
// always fits the container.
const MAX_CANVAS_HEIGHT = 640;

const LOCKED_FILL = '#6b7280';
const PIN_FILL = '#22c55e';
// Pin the pointer is hovering (and can remove) turns red with an ✕ so the
// remove action is obvious without relying on the cursor shape.
const REMOVE_FILL = '#ef4444';

const FloorPlan = ({
  img,
  spacesList = [],
  onPlacementsChange,
  initialPlacements = [],
  // Placement ids that are held by active bookings (edit flow): these pins
  // can't be moved or removed.
  lockedPlacementIds = [],
}) => {
  const [image] = useImage(img);
  const stageRef = useRef();
  // Zero-height full-width ruler used purely to measure the available column
  // width. Measuring the container that also holds the (wide) canvas creates
  // a feedback loop and, inside a <fieldset> (min-width: min-content), lets
  // the canvas inflate the column and overflow. The ruler can never be
  // widened by its siblings, so its width is always the true available space.
  const rulerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const { t } = useTranslation();
  const [placements, setPlacements] = useState(initialPlacements);
  const [selectedSpace, setSelectedSpace] = useState(null);
  const [confirmReset, setConfirmReset] = useState({
    isOpen: false,
    spaceName: '',
  });
  const [confirmRemoveId, setConfirmRemoveId] = useState(null);
  // Which pin the pointer is over — drives the "click to remove" affordance
  // (red ✕) so the crosshair alone doesn't have to communicate removal.
  const [hoveredPinId, setHoveredPinId] = useState(null);

  const lockedIds = useMemo(
    () => new Set(lockedPlacementIds),
    [lockedPlacementIds]
  );

  // Size the canvas to the available column width (measured via the ruler)
  // and follow resizes. Placements are percent-based, so pins survive any
  // resize.
  useEffect(() => {
    if (!rulerRef.current) return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect?.width;
      if (w) setContainerWidth(w);
    });
    ro.observe(rulerRef.current);
    return () => ro.disconnect();
  }, []);

  const imageSize = useMemo(() => {
    const ratio =
      image && image.naturalHeight > 0
        ? image.naturalWidth / image.naturalHeight
        : 4 / 3;
    // Before the first measurement, fall back to a sane default so the
    // canvas doesn't briefly render huge.
    let width = Math.max(1, containerWidth || 640);
    let height = width / ratio;
    if (height > MAX_CANVAS_HEIGHT) {
      height = MAX_CANVAS_HEIGHT;
      width = height * ratio;
    }
    // A very wide (short) plan could still compute a width wider than the
    // column once height is unclamped — never exceed the available width.
    if (containerWidth && width > containerWidth) {
      width = containerWidth;
      height = width / ratio;
    }
    return { width, height };
  }, [image, containerWidth]);

  // Prefer the real DB Space id (threaded through the edit flow as
  // `spaceId` to avoid react-hook-form's useFieldArray `id` keyName).
  // New spaces at creation time have none yet — the API links those
  // placements to Space rows by name on save.
  const enrichedSpaces = useMemo(
    () =>
      spacesList.map((space) => ({
        ...space,
        id: space.spaceId ?? space.id ?? uuidv4(),
      })),
    [spacesList]
  );

  // Availability is DERIVED from quantity minus current pins — never stored
  // as separate state, so it can't drift when spaces change underneath us.
  const placedCounts = useMemo(() => {
    const counts = new Map();
    for (const p of placements) {
      if (!p?.spaceName) continue;
      counts.set(p.spaceName, (counts.get(p.spaceName) || 0) + 1);
    }
    return counts;
  }, [placements]);

  const availabilityFor = (space) =>
    Math.max(
      0,
      (Number(space?.quantity) || 0) - (placedCounts.get(space?.name) || 0)
    );

  // Crosshair only means something when a space is picked and has room to
  // place — otherwise the canvas click is a no-op, so show the default cursor.
  const placingMode = !!selectedSpace && availabilityFor(selectedSpace) > 0;

  // Konva paints the cursor on the stage container; className can't target a
  // shape. Set it directly on hover so removable pins read as clickable and
  // locked ones as blocked, then hand control back to the className on leave.
  const setStageCursor = (e, cursor) => {
    const container = e.target?.getStage?.()?.container?.();
    if (container) container.style.cursor = cursor;
  };

  useEffect(() => {
    onPlacementsChange(placements);
  }, [placements, onPlacementsChange]);

  const handleCanvasClick = (e) => {
    if (e.target !== stageRef.current) return;
    if (!selectedSpace) return;

    const currentAvailable = availabilityFor(selectedSpace);
    if (currentAvailable <= 0) return;

    const stage = e.target.getStage();
    const pointer = stage.getPointerPosition();

    const newPlacement = {
      id: uuidv4(),
      spaceId: selectedSpace.id || null,
      spaceName: selectedSpace.name,
      xPercent: pointer.x / imageSize.width,
      yPercent: pointer.y / imageSize.height,
      canvasWidth: imageSize.width,
      canvasHeight: imageSize.height,
    };
    setPlacements((prev) => [...prev, newPlacement]);
    if (currentAvailable - 1 <= 0) setSelectedSpace(null);
  };

  const handleDragEnd = (id, e) => {
    setPlacements((prev) =>
      prev.map((p) =>
        p.id === id
          ? {
              ...p,
              xPercent: e.target.x() / imageSize.width,
              yPercent: e.target.y() / imageSize.height,
            }
          : p
      )
    );
  };

  // Click, tap, or right-click on a pin all ask to remove it — the confirm
  // modal makes an accidental click harmless, and tap is the only way to
  // delete a single pin on touch devices.
  const requestRemove = (id, e) => {
    if (e?.evt?.preventDefault) e.evt.preventDefault();
    if (lockedIds.has(id)) return;
    setConfirmRemoveId(id);
  };

  const handleConfirmRemove = () => {
    setPlacements((prev) => prev.filter((p) => p.id !== confirmRemoveId));
    setConfirmRemoveId(null);
  };

  const undoLastPlacement = (spaceName) => {
    // Last non-locked pin of this space.
    for (let i = placements.length - 1; i >= 0; i--) {
      const p = placements[i];
      if (p.spaceName === spaceName && !lockedIds.has(p.id)) {
        setPlacements((prev) => prev.filter((_, idx) => idx !== i));
        return;
      }
    }
  };

  const resetPlacements = (spaceName) => {
    const countToRemove = placements.filter(
      (p) => p.spaceName === spaceName && !lockedIds.has(p.id)
    ).length;
    if (countToRemove === 0) return;
    setConfirmReset({ isOpen: true, spaceName });
  };

  const handleConfirmReset = () => {
    const { spaceName } = confirmReset;
    setPlacements((prev) =>
      prev.filter((p) => p.spaceName !== spaceName || lockedIds.has(p.id))
    );
    setConfirmReset({ isOpen: false, spaceName: '' });
  };

  return (
    <div className="flex flex-col items-center p-2 w-full min-w-0">
      {/* Measurement ruler — never inflated by the canvas (see rulerRef). */}
      <div ref={rulerRef} className="w-full h-0" aria-hidden="true" />
      <div className="flex flex-wrap justify-center gap-3 mb-3 w-full">
        {enrichedSpaces.map((space, idx) => {
          const available = availabilityFor(space);
          const isSelected = selectedSpace?.name === space.name;
          const removablePlacements = placements.some(
            (p) => p.spaceName === space.name && !lockedIds.has(p.id)
          );

          return (
            <div
              key={idx}
              className={`inline-flex items-center gap-1 rounded-full border pl-1 pr-1 py-1 transition ${
                isSelected
                  ? 'border-yellow-500 bg-yellow-500 text-white shadow-sm'
                  : available === 0
                    ? 'border-gray-200 bg-gray-100 text-gray-400'
                    : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              <button
                type="button"
                onClick={() => available > 0 && setSelectedSpace(space)}
                disabled={available === 0}
                className={`flex items-center gap-2 rounded-full px-3 py-1 text-left ${
                  available === 0 ? 'cursor-not-allowed' : ''
                }`}
                title={
                  available === 0
                    ? t('event_create.all_placed', 'All spots placed')
                    : undefined
                }
              >
                <span className="text-sm font-bold leading-none">
                  {space.name}
                </span>
                <span
                  className={`text-xs leading-none ${
                    isSelected ? 'text-white/80' : 'text-gray-500'
                  }`}
                >
                  {`${available} ${t('event_create.availability')}`}
                </span>
              </button>

              {removablePlacements && (
                <span className="flex items-center gap-0.5">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      undoLastPlacement(space.name);
                    }}
                    title={t('event_create.undo_last_pin', 'Undo last pin')}
                    className={`grid place-items-center w-6 h-6 rounded-full transition ${
                      isSelected ? 'hover:bg-white/20' : 'hover:bg-gray-100'
                    }`}
                  >
                    <ArrowUturnLeftIcon
                      className={`w-4 h-4 ${isSelected ? 'text-white' : 'text-blue-600'}`}
                    />
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      resetPlacements(space.name);
                    }}
                    title={t('event_create.clear_pins', 'Clear all pins')}
                    className={`grid place-items-center w-6 h-6 rounded-full transition ${
                      isSelected ? 'hover:bg-white/20' : 'hover:bg-gray-100'
                    }`}
                  >
                    <XMarkIcon
                      className={`w-4 h-4 ${isSelected ? 'text-white' : 'text-red-600'}`}
                    />
                  </button>
                </span>
              )}
            </div>
          );
        })}
      </div>

      {image && (
        <>
          <div
            className="mx-auto overflow-hidden rounded-xl border border-gray-200 bg-gray-50 shadow-sm"
            style={{ width: imageSize.width, maxWidth: '100%', maxHeight: '80vh' }}
          >
            <Stage
              ref={stageRef}
              width={imageSize.width}
              height={imageSize.height}
              className={placingMode ? 'cursor-crosshair' : 'cursor-default'}
              onMouseDown={handleCanvasClick}
              onTouchStart={handleCanvasClick}
            >
              <Layer>
                <KonvaImage
                  image={image}
                  width={imageSize.width}
                  height={imageSize.height}
                  listening={false}
                />
                {placements.map((p) => {
                  const space = enrichedSpaces.find(
                    (s) => s.id === p.spaceId || s.name === p.spaceName
                  );
                  const initials =
                    space?.name?.substring(0, 2).toUpperCase() ||
                    p.spaceName?.substring(0, 2).toUpperCase() ||
                    '??';
                  const locked = lockedIds.has(p.id);
                  const removeHint = hoveredPinId === p.id && !locked;
                  const x = p.xPercent * imageSize.width;
                  const y = p.yPercent * imageSize.height;

                  return (
                    <React.Fragment key={p.id}>
                      <Circle
                        x={x}
                        y={y}
                        radius={removeHint ? 13 : 12}
                        fill={
                          locked
                            ? LOCKED_FILL
                            : removeHint
                              ? REMOVE_FILL
                              : PIN_FILL
                        }
                        stroke={removeHint ? '#ffffff' : '#1f2937'}
                        strokeWidth={2}
                        draggable={!locked}
                        onDragStart={() => setHoveredPinId(null)}
                        onDragEnd={(e) => !locked && handleDragEnd(p.id, e)}
                        onClick={(e) => requestRemove(p.id, e)}
                        onTap={(e) => requestRemove(p.id, e)}
                        onContextMenu={(e) => requestRemove(p.id, e)}
                        onMouseEnter={(e) => {
                          setHoveredPinId(p.id);
                          setStageCursor(e, locked ? 'not-allowed' : 'pointer');
                        }}
                        onMouseLeave={(e) => {
                          setHoveredPinId((cur) => (cur === p.id ? null : cur));
                          // Hand the cursor back to the stage className.
                          setStageCursor(e, '');
                        }}
                      />
                      <Text
                        x={x}
                        y={y}
                        text={removeHint ? '✕' : initials}
                        fontSize={removeHint ? 13 : 10}
                        fill="#fff"
                        fontStyle="bold"
                        align="center"
                        verticalAlign="middle"
                        offsetX={removeHint ? 4 : 7}
                        offsetY={removeHint ? 6 : 4}
                        listening={false}
                      />
                    </React.Fragment>
                  );
                })}
              </Layer>
            </Stage>
          </div>
          <p className="mt-2 text-[11px] text-gray-500 text-center">
            {t(
              'event_create.floorplan_pin_hint',
              'Drag a pin to move it · click or tap a pin to remove it'
            )}
            {lockedIds.size > 0 && (
              <>
                {' · '}
                {t(
                  'event_create.floorplan_pin_locked_hint',
                  'Gray pins have active bookings and are locked'
                )}
              </>
            )}
          </p>
        </>
      )}

      <ConfirmationModal
        isOpen={confirmRemoveId != null}
        title={t('event_create.confirm_remove_pin', 'Remove this pin?')}
        message={t(
          'event_create.confirm_remove_pin_msg',
          'The spot goes back to the available count for its space.'
        )}
        onConfirm={handleConfirmRemove}
        onCancel={() => setConfirmRemoveId(null)}
      />

      <ConfirmationModal
        isOpen={confirmReset.isOpen}
        title={t('event_create.confirm_reset')}
        message={t('event_create.confirm_reset_msg', {
          spaceName: confirmReset.spaceName || 'Unknown',
        })}
        onConfirm={handleConfirmReset}
        onCancel={() => setConfirmReset({ isOpen: false, spaceName: '' })}
      />
    </div>
  );
};

export default FloorPlan;
